import * as React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import styled, { css } from "styled-components";
import useResizeObserver from "@react-hook/resize-observer";
import { ButtonToolbar } from "components/bootstrap";
import { ACTIONS_COL_ID, CELL_PADDING } from "components/common/EntityDataTable/Constants";
import { actionsHeaderWidthVar } from "components/common/EntityDataTable/CSSVariables";
const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 100%;
`;
const BackgroundFoundation = styled.div(
  ({ theme, $parentBgColor }) => css`
    background-color: ${$parentBgColor != null ? $parentBgColor : theme.colors.global.contentBackground};
    height: 100%;
    width: var(${actionsHeaderWidthVar});
  `
);
const Actions = styled.div(
  ({ $isEvenRow, theme }) => css`
    display: flex;
    justify-content: flex-end;
    padding: ${CELL_PADDING}px;
    background: ${$isEvenRow ? theme.colors.table.row.background : theme.colors.table.row.backgroundStriped};
    height: 100%;
    align-items: flex-start;
  `
);
const ActionCell = ({
  row,
  entityActions,
  onWidthChange,
  parentBgColor
}) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) {
      onWidthChange(row.id, ref.current.getBoundingClientRect().width);
    }
  }, [row.id, onWidthChange]);
  useResizeObserver(ref, ({ contentRect: { width } }) => onWidthChange(row.id, width));
  return /* @__PURE__ */ React.createElement(AlignRight, null, /* @__PURE__ */ React.createElement(BackgroundFoundation, { $parentBgColor: parentBgColor }, /* @__PURE__ */ React.createElement(Actions, { $isEvenRow: row.index % 2 === 0 }, /* @__PURE__ */ React.createElement(ButtonToolbar, { ref }, entityActions(row.original)))));
};
const useActionsColumnDefinition = ({
  colWidth,
  entityActions,
  hasRowActions,
  onWidthChange,
  parentBgColor
}) => {
  const columnHelper = createColumnHelper();
  const cell = useCallback(
    ({ row }) => entityActions ? /* @__PURE__ */ React.createElement(
      ActionCell,
      {
        row,
        entityActions,
        onWidthChange,
        parentBgColor
      }
    ) : null,
    [entityActions, onWidthChange, parentBgColor]
  );
  const header = useCallback(
    () => /* @__PURE__ */ React.createElement(AlignRight, null, /* @__PURE__ */ React.createElement(BackgroundFoundation, { $parentBgColor: parentBgColor })),
    [parentBgColor]
  );
  return useMemo(
    () => columnHelper.display({
      id: ACTIONS_COL_ID,
      size: colWidth,
      enableHiding: false,
      enablePinning: true,
      enableResizing: false,
      header: hasRowActions ? header : void 0,
      cell: hasRowActions ? cell : void 0,
      meta: {
        hideCellPadding: true
      }
    }),
    [colWidth, cell, columnHelper, hasRowActions, header]
  );
};
export default useActionsColumnDefinition;
